import React, { useEffect, useState, useCallback, useRef } from 'react';
import { SearchAgentResult } from '../../../system/search';
import { useService } from '../../../contexts/ServiceContext';
import { useSidePanel } from '../../../contexts/SidePanelContext';
import { useAuth } from '../../../contexts/AuthContext';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  TextField,
  Avatar,
  IconButton,
  Autocomplete,
  Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { debounce } from 'lodash';
import { useMetadata } from '../../../contexts/MetadataContext';
import {
  FormFieldContext,
  FormFieldContextValue,
} from '../../edit/FormFieldContext';
import { FormFieldLabel } from '../../edit/FormFieldLabel';
import {
  isPersonalAssistant,
  getListAgentPermissionLevel,
  getOfficeKeysFromPermissions,
} from '../../../contexts/util/createParams';
import { PermissionLevel } from '../../../system/permissions';
import { generateDataTestId } from '../../../utils';
import { useQueryMemberLicenses } from '../../../hooks/useQueryMemberLicenses';
import { useTenant } from '../../../contexts/TenantContext';

const getAvatarImage = (agent: SearchAgentResult) => {
  if (typeof agent.AgentPhoto === 'string') {
    return agent.AgentPhoto;
  }
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(agent.MemberFullName)}`;
};

export const AgentForm: React.FC<{
  onSave: (agent: SearchAgentResult) => void;
  initialAgent?: SearchAgentResult;
  agentType: 'List' | 'Co-List' | 'Selling' | 'Co-Selling';
  isScopedToBrokerage: boolean;
  listingStateOrProvince: string;
}> = ({
  onSave,
  initialAgent,
  agentType,
  isScopedToBrokerage,
  listingStateOrProvince,
}) => {
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState<SearchAgentResult[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<
    SearchAgentResult | undefined
  >(initialAgent);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingLicense, setIsLoadingLicense] = useState(true);
  const [licenseNumber, setLicenseNumber] = useState<string | undefined>(
    undefined,
  );
  const [licenseExpiry, setLicenseExpiry] = useState<string | undefined>(
    undefined,
  );
  const { findAgent } = useService();
  const { session } = useAuth();
  const { getConfigValue } = useTenant();
  const abortControllerRef = useRef<AbortController | null>(null);

  const { field } = useMetadata();
  const { queryMemberLicenses } = useQueryMemberLicenses();
  const getAgentHelpContext = (agentType: string): FormFieldContextValue => {
    return {
      fieldMeta: field(
        `${agentType.replace('Selling', 'Buyer').replace('-', '_')}_AGENT_MLS_ID`.toUpperCase(), // this maps to fieldMeta based on agentType
      ),
      isRequired: true,
      field: {
        Label: `${agentType} Agent`,
      },
      fieldPresentation: {},
    } as FormFieldContextValue;
  };

  // Check if licenses are enabled for tenant
  const enableLicenses = getConfigValue<boolean>('ENABLE_LICENSES');

  // Check if user is a personal assistant
  const isPA = isPersonalAssistant(session);

  // Get permission level for list agent searches
  const permissionLevel = getListAgentPermissionLevel(session);

  // Get office keys if permission is at office level
  const officeKeys = getOfficeKeysFromPermissions(session);

  const searchAgent = useCallback(
    async (inputValue: string) => {
      if (inputValue.length < 3) {
        setSearchResults([]);
        return;
      }

      // Cancel any ongoing requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();

      setIsLoading(true);
      setError(null);

      try {
        const results = await findAgent(
          inputValue,
          isScopedToBrokerage,
          abortControllerRef.current.signal,
        );

        // Clear previous results first
        setSearchResults([]);

        let filteredResults = results;

        // Apply permission-based filtering for List agents
        if (agentType === 'List') {
          if (
            permissionLevel === PermissionLevel.OFFICE &&
            officeKeys.length > 0
          ) {
            // Filter by office keys if permission is office level
            filteredResults = results.filter((agent) =>
              officeKeys.includes(agent.OfficeKey),
            );
          }

          // Filter out current user if they're a personal assistant
          if (isPA) {
            filteredResults = filteredResults.filter(
              (agent) => agent.MemberMlsId !== session?.user.memberMlsId,
            );
          }
        }

        const uniqueResults = filteredResults.reduce(
          (acc: SearchAgentResult[], current) => {
            const exists = acc.find(
              (item) => item.MemberMlsId === current.MemberMlsId,
            );
            if (!exists) {
              acc.push(current);
            }
            return acc;
          },
          [],
        );

        setSearchResults(uniqueResults);
      } catch (err) {
        // Only set error if it's not an abort error
        if (err && (err as any).name !== 'AbortError') {
          setError('An error occurred while searching. Please try again.');
          setSearchResults([]);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [findAgent, isScopedToBrokerage],
  );

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      searchAgent(value);
    }, 300),
    [searchAgent],
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    const fetchInitialAgentData = async () => {
      if (
        initialAgent &&
        !initialAgent.AgentPhoto &&
        initialAgent.MemberMlsId
      ) {
        setIsLoading(true);
        try {
          const results = await findAgent(initialAgent.MemberMlsId);
          if (results.length > 0) {
            const updatedAgent = {
              ...initialAgent,
              ...results[0],
            };
            setSelectedAgent(updatedAgent);
            if (updatedAgent.MemberMlsId !== initialAgent.MemberMlsId) {
              onSave(updatedAgent);
            }
          }
        } catch (err) {
          console.error('Error fetching initial agent data:', err);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchInitialAgentData();
  }, [initialAgent, findAgent, onSave]);

  const handleSave = () => {
    if (selectedAgent) {
      onSave(selectedAgent);
    }
  };

  useEffect(() => {
    queryMemberLicenses({
      memberKey: selectedAgent?.MemberKey,
      stateOrProvince: listingStateOrProvince,
      setLicenseNumber,
      setLicenseExpiry,
      setIsLoading: setIsLoadingLicense,
    });
  }, [selectedAgent?.MemberKey, listingStateOrProvince]);

  const { field: fieldContext, fieldMeta } = getAgentHelpContext(agentType);

  return (
    <>
      <Box sx={{ marginTop: 2, alignItems: 'center', p: 2 }}>
        <FormFieldContext.Provider value={getAgentHelpContext(agentType)}>
          <FormFieldLabel />
        </FormFieldContext.Provider>
        <Autocomplete
          data-testid={generateDataTestId(fieldContext, fieldMeta, 'textField')}
          options={searchResults}
          isOptionEqualToValue={(
            option,
            value, // Add this line
          ) => option.MemberMlsId === value?.MemberMlsId}
          filterOptions={(options) => options}
          getOptionLabel={(option) => option.MemberFullName}
          onInputChange={(event, newInputValue) => {
            setSearchText(newInputValue);
            debouncedSearch(newInputValue);
          }}
          onChange={(event, newValue) => {
            setSelectedAgent(newValue || undefined);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              placeholder="Search for agent by name or MLS ID"
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.MemberKey}>
              <Box
                display="flex"
                alignItems="center"
                width="100%"
                sx={{
                  borderBottom: searchResults.length <= 1 ? '0' : '1px solid',
                  borderColor: 'grey.300',
                  paddingBottom: searchResults.length <= 1 ? 0 : 2,
                }}
              >
                <Avatar src={getAvatarImage(option)} sx={{ mr: 2 }} />
                <Box flexGrow={1}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'grey.700',
                        fontSize: '16px',
                        fontWeight: '700',
                      }}
                    >
                      {option.MemberFullName}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {option.MemberMlsId}
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography
                      sx={{
                        color: 'grey.500',
                        fontSize: '14px',
                        fontWeight: '400',
                      }}
                    >
                      {option.OfficeName}
                    </Typography>
                    <Typography
                      sx={{
                        color: 'grey.500',
                        fontSize: '14px',
                        fontWeight: '400',
                      }}
                    >
                      {option.OfficeMlsId}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </li>
          )}
          loading={isLoading}
          sx={{ mb: 2 }}
        />

        {error && (
          <Typography color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}

        {selectedAgent && (
          <>
            <Divider sx={{ mt: 4 }} />
            <Typography
              sx={{
                mt: 4,
                fontWeight: '500',
                fontSize: '21px',
                color: 'grey.700',
              }}
            >
              Selected {agentType} Agent
            </Typography>
          </>
        )}

        {selectedAgent && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              border: '1px solid',
              borderColor: 'grey.200',
              borderRadius: 1,
              position: 'relative',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar
                src={getAvatarImage(selectedAgent)}
                sx={{ width: 50, height: 50, mr: 2 }}
              />
              <Box>
                <Typography
                  sx={{
                    fontWeight: '600',
                    fontSize: '18px',
                    color: 'grey.700',
                  }}
                >
                  {selectedAgent.MemberFullName}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: '400',
                    fontSize: '14px',
                    color: 'grey.500',
                  }}
                >
                  {agentType} Agent
                </Typography>
              </Box>
              <IconButton
                onClick={() => setSelectedAgent(undefined)}
                sx={{ position: 'absolute', top: 8, right: 8 }}
              >
                <DeleteIcon sx={{ color: 'error.medium' }} />
              </IconButton>
            </Box>

            {enableLicenses && (
              <>
                {isLoadingLicense && <CircularProgress size={20} />}
                {!isLoadingLicense && (
                  <Typography variant="body2" sx={{ color: 'grey.700' }}>
                    License Number: {licenseNumber ?? 'Unknown'}
                  </Typography>
                )}
                {!isLoadingLicense && licenseNumber && (
                  <Typography variant="body2" sx={{ color: 'grey.700' }}>
                    License Expiration: {licenseExpiry}
                  </Typography>
                )}
              </>
            )}
          </Box>
        )}
      </Box>
      <Box
        sx={{
          padding: '16px',
          borderTop: '1px solid',
          borderColor: 'grey.300',
          backgroundColor: 'background.paper',
          position: 'absolute',
          bottom: 0,
          width: '100%',
        }}
      >
        <Button
          onClick={handleSave}
          disabled={!selectedAgent}
          variant="contained"
          color="primary"
          fullWidth
          sx={{
            textTransform: 'capitalize',
          }}
        >
          Save
        </Button>
      </Box>
    </>
  );
};
